import { InfoOutlineIcon } from '@carboncopyco/icons';
import type { ShareSecurityLevel } from '@carboncopyco/types';
import {
  Checkbox,
  ColorHex,
  MessageBox,
  TextBase,
  TextCaption,
  Tooltip,
} from '@carboncopyco/ui';
import { useBoolean } from 'ahooks';
import { Field, type FieldProps } from 'formik';
import { useRef } from 'react';
import { useIntegration } from '../Integrations';

type SlackIntegrationFieldProps = {
  securityLevel: ShareSecurityLevel;
};

export const SlackIntegrationField = ({
  securityLevel,
}: SlackIntegrationFieldProps) => {
  const infoRef = useRef(null);

  const { renewalRequired, integrationId, paused } = useIntegration({
    integrationType: 'slack',
  });
  const [showRenewalMessage, setShowRenewalMessage] = useBoolean(true);

  if (securityLevel === 'internal' || !integrationId || paused) {
    return null;
  }

  if (renewalRequired) {
    if (showRenewalMessage) {
      return (
        <div className="pt-3 pb-4">
          <MessageBox
            danger
            close
            handleClose={setShowRenewalMessage.setFalse}
            label="Your Slack integration failed. Contact the admin to reconnect it."
          />
        </div>
      );
    }
    return null;
  }

  return (
    <Field type="checkbox">
      {({ field: { checked, ...rest }, form }: FieldProps) => (
        <div className="flex items-center p-2 gap-1">
          <Checkbox
            {...rest}
            checked={form.values.slackIntegration}
            onChange={() => {
              form.setFieldValue(
                'slackIntegration',
                !form.values.slackIntegration
              );
            }}
            label={
              <TextBase
                color="text-ds-natural-600"
                label="Enable new lead notification on Slack"
              />
            }
          />
          <div ref={infoRef}>
            <InfoOutlineIcon
              size={16}
              color={ColorHex.natural}
            />
          </div>
          <Tooltip
            placement="top"
            trigger="mouseenter"
            hideOnClick="toggle"
            anchorRef={infoRef}
            noArrow
          >
            <div className="w-ds-64 py-2 px-2.5">
              <TextCaption label="Receive notifications in Demostack’s Slack integration channel for every visitor entering this link" />
            </div>
          </Tooltip>
        </div>
      )}
    </Field>
  );
};
